import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { CardTile } from "./Bits";
import { inputCls } from "./JoinSteps";

export const favoriteSets = (cards) => {
  const by = {};
  cards.forEach((c) => { by[c.set_id] = by[c.set_id] || { ...c, count: 0 }; by[c.set_id].count += c.quantity || 1; });
  return Object.values(by).sort((a, b) => b.count - a.count).slice(0, 4);
};

export const SetTiles = ({ cards }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="favorite-sets">
    {favoriteSets(cards).map((s) => (
      <div key={s.set_id} className="rounded-2xl bg-[#120E1F] p-4 text-center space-y-2" data-testid={`fav-set-${s.set_id}`}>
        {s.set_logo ? <img src={s.set_logo} alt={s.set_name} className="h-12 mx-auto object-contain" loading="lazy" /> : <div className="h-12" />}
        <p className="text-[11px] text-white/70 truncate">{s.set_name}</p>
        <p className="text-[10px] font-mono text-[#F2C230]">{s.count}×</p>
      </div>
    ))}
  </div>
);

export const CollectionManager = ({ cards, reload }) => {
  const { tr } = useLang();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchPage = async (n) => {
    setLoading(true);
    try {
      const { data } = await api.get("/tcg/search", { params: { q, page: n } });
      setResults((prev) => (n === 1 ? data.cards : [...(prev || []), ...data.cards]));
      setPage(n); setHasMore(data.has_more);
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  const search = (e) => { e.preventDefault(); fetchPage(1); };
  const add = async (c) => {
    setAdding(c.card_id);
    try { const { data } = await api.post("/collection", { card_id: c.card_id }); toast.success(data.points ? `+${data.points} ${tr("Punkte", "points")} · ${c.name}` : `${c.name} ×${data.quantity}`); reload(); }
    catch (err) { toast.error(errMsg(err)); } finally { setAdding(""); }
  };
  const remove = async (c) => { await api.delete(`/collection/${c.card_id}`); reload(); };

  return (
    <div className="space-y-8" data-testid="collection-manager">
      <div className="bg-white rounded-2xl border border-[#120E1F]/5 p-5 space-y-4">
        <h3 className="font-display text-2xl">{tr("Karte hinzufügen", "Add a card")}</h3>
        <form onSubmit={search} className="flex gap-3">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#707783]" />
            <input required minLength={2} value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Kartenname, z.B. Gengar", "Card name, e.g. Gengar")} data-testid="tcg-search-input" className={`${inputCls} pl-10`} /></div>
          <button className="btn-dark" disabled={loading} data-testid="tcg-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Suchen", "Search")}</button>
        </form>
        <p className="text-[11px] text-[#707783] -mt-2">{tr("Deutsche oder englische Namen, optional mit Kartennummer – z.B. „Glurak 4“, „Nachtara“, „Pikachu 25“.", "German or English names, optionally with card number – e.g. \"Charizard 4\", \"Umbreon\".")}</p>
        {results && results.length === 0 && <p className="text-sm text-[#707783]" data-testid="tcg-no-results">{tr("Keine Karten gefunden.", "No cards found.")}</p>}
        {results && results.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4 max-h-[520px] overflow-y-auto pr-1" data-testid="tcg-results">
            {results.map((c) => (
              <button key={c.card_id} onClick={() => add(c)} disabled={adding === c.card_id} data-testid={`tcg-add-${c.card_id}`} className="text-left group relative">
                <CardTile c={c} />
                <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[#F2C230] text-[#120E1F] grid place-items-center shadow group-hover:scale-110 transition-transform">{adding === c.card_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-4 w-4" />}</span>
              </button>
            ))}
          </div>
        )}
        {hasMore && <button onClick={() => fetchPage(page + 1)} disabled={loading} className="btn-outline w-full" data-testid="tcg-load-more">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : tr("Mehr laden", "Load more")}</button>}
        <p className="text-[11px] text-[#707783]">{tr("+20 Punkte pro neuer Karte (bis 50 Karten). Kartendaten: Pokémon TCG API.", "+20 points per new card (up to 50 cards). Card data: Pokémon TCG API.")}</p>
      </div>
      <div className="space-y-4">
        <h3 className="font-display text-2xl">{tr("Meine Sammlung", "My collection")} <span className="font-mono text-base text-[#6E3FB8]" data-testid="collection-count">({cards.length})</span></h3>
        {cards.length === 0 && <p className="text-sm text-[#707783]">{tr("Noch keine Karten – such oben nach deinem ersten Hit!", "No cards yet – search above for your first hit!")}</p>}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">{cards.map((c) => <CardTile key={c.card_id} c={c} onRemove={() => remove(c)} testid={`my-card-${c.card_id}`} />)}</div>
      </div>
    </div>
  );
};

export const useCollection = (enabled = true) => {
  const [cards, setCards] = useState([]);
  const reload = useCallback(() => (enabled ? api.get("/collection").then((r) => setCards(r.data)).catch(() => setCards([])) : Promise.resolve()), [enabled]);
  useEffect(() => { reload(); }, [reload]);
  return [cards, reload];
};

export const ViewAll = ({ to, label }) => <Link to={to} className="text-xs font-semibold text-[#6E3FB8] hover:text-[#120E1F] transition-colors">{label} →</Link>;
